"use client";

import { useEffect, useState } from "react";
import ReggieCredits from "./ReggieCredits";
import { STORE_ANNOUNCEMENTS as ANNOUNCEMENTS, STORE_MENU, storeHref } from "../../lib/store_sync";

const STORE_URL = process.env.NEXT_PUBLIC_STORE_URL || "https://recruitgraphics.com";
// Announcement bar and menu copied from the live store; a daily check keeps
// them in step (lib/store_sync.js).
const MENU = (base) => STORE_MENU.map((m) => m.items
  ? { ...m, items: m.items.map((it) => ({ ...it, href: storeHref(it.href, base) })) }
  : { ...m, href: storeHref(m.href, base) });

function SearchIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}
function AccountIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden>
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
function CartIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden>
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
      <path d="M3 6h18" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}
function ContactIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2" y="4" width="20" height="16" rx="2"/>
      <polyline points="2,4 12,13 22,4"/>
    </svg>
  );
}
function Chevron({ dir = "down", size = 10 }) {
  const d = { down: "M2 4l4 4 4-4", left: "M8 2L4 6l4 4", right: "M4 2l4 4-4 4" }[dir];
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden>
      <path d={d} />
    </svg>
  );
}
function MenuIcon({ open }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden>
      {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M3 6h18M3 12h18M3 18h18" />}
    </svg>
  );
}

function AnnouncementBar() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % ANNOUNCEMENTS.length), 5000);
    return () => clearInterval(t);
  }, [i]);
  const step = (d) => setI((n) => (n + d + ANNOUNCEMENTS.length) % ANNOUNCEMENTS.length);
  return (
    <div className="rg-announce">
      <button type="button" onClick={() => step(-1)} aria-label="Previous announcement"><Chevron dir="left" /></button>
      <p aria-live="polite">{ANNOUNCEMENTS[i]}</p>
      <button type="button" onClick={() => step(1)} aria-label="Next announcement"><Chevron dir="right" /></button>
    </div>
  );
}

const CSS = `
  .rg-announce { width: 100%; max-width: 1600px; margin: 0 auto; padding: 0 50px; box-sizing: border-box;
    display: flex; align-items: center; justify-content: space-between; height: 38px; }
  .rg-announce p { margin: 0; font: 300 15px/1.2 "Open Sans Condensed", "Figtree", sans-serif;
    letter-spacing: 0.6px; text-align: center; color: #ede8e0; }
  .rg-announce button { background: none; border: 0; color: #ede8e0; cursor: pointer; padding: 6px; display: flex; }

  .rg-header-row { max-width: 1600px; margin: 0 auto; padding: 10px 50px 14px; box-sizing: border-box;
    display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; }
  .rg-nav { display: flex; align-items: center; gap: 28px; }
  .rg-nav > div { position: relative; }
  .rg-nav-link { background: none; border: 0; padding: 6px 0; cursor: pointer; color: #ede8e0;
    font: 500 14px/1.2 "Figtree", system-ui, sans-serif; letter-spacing: 0.4px; text-decoration: none;
    display: inline-flex; align-items: center; gap: 6px; }
  .rg-nav-link:hover { text-decoration: underline; text-underline-offset: 3px; }
  .rg-dropdown { position: absolute; top: calc(100% + 10px); left: -20px; z-index: 50; min-width: 200px;
    background: #000; border: 1px solid rgba(237,232,224,0.12); padding: 12px 0; }
  .rg-dropdown a { display: block; padding: 8px 20px; color: #ede8e0; text-decoration: none;
    font: 500 14px/1.2 "Figtree", system-ui, sans-serif; }
  .rg-dropdown a:hover { text-decoration: underline; text-underline-offset: 3px; }
  .rg-icons { display: flex; align-items: center; justify-content: flex-end; gap: 22px; }
  .rg-icons a { color: #ede8e0; display: flex; }
  .rg-burger { display: none; background: none; border: 0; color: #ede8e0; cursor: pointer; padding: 4px; }
  .rg-drawer { display: none; }

  @media (max-width: 1199px) {   /* four menu items need ~1200px beside the logo */
    .rg-announce, .rg-header-row { padding-left: 16px; padding-right: 16px; }
    .rg-nav { display: none; }
    .rg-burger { display: flex; }
    .rg-icons { gap: 16px; }
    .rg-icons .rg-hide-sm { display: none; }
    .rg-drawer { display: block; border-top: 1px solid rgba(237,232,224,0.12); padding: 8px 16px 16px; }
    .rg-drawer a, .rg-drawer span { display: block; padding: 10px 0; color: #ede8e0; text-decoration: none;
      font: 500 15px/1.2 "Figtree", system-ui, sans-serif; }
    .rg-drawer span { color: #9a9189; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; padding-top: 14px; }
    .rg-drawer .rg-sub { padding-left: 14px; }
  }
`;

export default function StoreHeader() {
  const base = STORE_URL.replace(/\/$/, "");
  const menu = MENU(base);
  const [openMenu, setOpenMenu] = useState(null);
  const [drawer, setDrawer] = useState(false);

  // Dropdowns close on any click outside them, as the store's <details> do.
  useEffect(() => {
    if (openMenu == null) return;
    const close = (e) => { if (!e.target.closest?.(".rg-nav")) setOpenMenu(null); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openMenu]);

  return (
    <>
      {/* Raw, not a text child: SSR escapes the ">" combinator and hydration then mismatches. */}
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <AnnouncementBar />
      <header style={{ position: "relative", backgroundColor: "#000000" }}>
        <div className="rg-header-row">
          <div>
            <button type="button" className="rg-burger" onClick={() => setDrawer((d) => !d)} aria-label="Menu" aria-expanded={drawer}>
              <MenuIcon open={drawer} />
            </button>
            <nav className="rg-nav" aria-label="Main">
              {menu.map((m) => m.items ? (
                <div key={m.label}>
                  <button type="button" className="rg-nav-link" aria-expanded={openMenu === m.label}
                    onClick={() => setOpenMenu((o) => (o === m.label ? null : m.label))}>
                    {m.label} <Chevron />
                  </button>
                  {openMenu === m.label && (
                    <div className="rg-dropdown">
                      {m.items.map((it) => <a key={it.label} href={it.href}>{it.label}</a>)}
                    </div>
                  )}
                </div>
              ) : (
                <div key={m.label}><a className="rg-nav-link" href={m.href}>{m.label}</a></div>
              ))}
            </nav>
          </div>

          <a href={base} style={{ display: "block" }} aria-label="Recruit Graphics home">
            <img
              src="/RGLogo.png"
              alt="Recruit Graphics"
              width={300}
              className="store-header-logo"
              style={{ maxWidth: "100%", height: "auto", display: "block" }}
            />
          </a>

          <div className="rg-icons">
            <a href={`${base}/search`} aria-label="Search"><SearchIcon /></a>
            <a href={`${base}/account`} className="rg-hide-sm" aria-label="Account"><AccountIcon /></a>
            <a href={`${base}/pages/contact`} className="rg-hide-sm" aria-label="Contact"><ContactIcon /></a>
            <a href={`${base}/cart`} aria-label="Cart"><CartIcon /></a>
          </div>
        </div>

        {drawer && (
          <nav className="rg-drawer" aria-label="Main">
            {menu.map((m) => m.items ? (
              <div key={m.label}>
                <span>{m.label}</span>
                {m.items.map((it) => <a key={it.label} className="rg-sub" href={it.href}>{it.label}</a>)}
              </div>
            ) : (
              <a key={m.label} href={m.href}>{m.label}</a>
            ))}
            <a href={`${base}/account`}>Account</a>
            <a href={`${base}/pages/contact`}>Contact</a>
          </nav>
        )}
        <ReggieCredits />
      </header>
    </>
  );
}
